<script lang="ts">
    import { page } from "$app/stores"
    import { onMount } from "svelte"
    import { deviceSize, readerFrameMode } from "@utils/material/responsive"
    import { audioPlayerHeight } from "@components/audiobook/Player.svelte"
    import Navigator, {
        navigatorOpen,
        navigatorWidth,
    } from "@components/navigator/Navigator.svelte"
    import PrimaryNavigation from "./PrimaryNavigation.svelte"
    import { collapseReadingTabPanel, readingTabPanelMode } from "./ReadingTabPanel.svelte"
    import { readerFocusMode } from "@utils/reading/reader-focus"

    onMount(() => {
        $navigatorWidth = 0
    })

    $: readSection = $page.url.pathname.startsWith("/book")

    $: if (!readSection && $navigatorOpen) {
        $navigatorOpen = false
    }

    $: if (!readSection && $readingTabPanelMode !== "collapsed") {
        collapseReadingTabPanel()
    }

    $: readingTabsWidth =
        readSection && !$readerFocusMode && $deviceSize === "desktop"
            ? $readingTabPanelMode === "side"
                ? "13.25rem"
                : "3.65rem"
            : "0rem"
    $: readingTabsDockHeight =
        readSection && !$readerFocusMode && $deviceSize !== "desktop"
            ? `calc(3rem + ${$audioPlayerHeight}px)`
            : "0rem"
</script>

<div
    class="app-shell"
    class:secondary-open={readSection && $navigatorOpen}
    class:reader-layout-compact={$readerFrameMode === "compact"}
    class:reader-focus={readSection && $readerFocusMode}
    style={`--app-reading-tabs-width: ${readingTabsWidth}; --app-reader-tabs-dock-height: ${readingTabsDockHeight};`}
>
    <PrimaryNavigation readingMode={readSection} />
    {#if readSection}
        <Navigator />
    {/if}
    <main class="app-main">
        <slot />
    </main>
</div>

<style>
    .app-shell {
        --app-primary-nav-width: 5.25rem;
        --app-primary-nav-height: calc(4.75rem + var(--safe-area-inset-bottom));
        --app-reader-tabs-dock-height: 0rem;
        --app-navigation-width: calc(var(--app-primary-nav-width) + var(--app-reading-tabs-width));

        grid-area: main;
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
        display: grid;
        grid-template-columns: var(--app-navigation-width) minmax(0, 1fr);
        grid-template-rows: minmax(0, 1fr);
        grid-template-areas: "primary main";
        background: var(--background);
        color: var(--color-grey-900);
        position: relative;
        transition:
            grid-template-columns 0.24s ease,
            grid-template-rows 0.24s ease;
    }

    .app-main {
        grid-area: main;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
        position: relative;
        background: var(--background);
    }

    :global(.darkmode) .app-shell,
    :global(.darkmode) .app-main {
        background: var(--background);
        color: var(--color-grey-50);
    }

    :global(#navigator) {
        z-index: 12;
    }

    :global(html.expanded) .app-shell {
        grid-template-columns: 0 minmax(0, 1fr);
    }

    :global(html.expanded) .app-shell :global(#navigator),
    :global(html.expanded) .app-shell :global(.reading-tab-panel),
    :global(html.expanded) .app-shell :global(.primary-navigation) {
        display: none;
    }

    .app-shell.reader-focus {
        grid-template-columns: 0 minmax(0, 1fr);
    }

    /* Keep this container untransformed: its mobile tab strip is viewport-fixed. */
    .app-shell :global(.primary-navigation) {
        opacity: 1;
        visibility: visible;
        transition:
            opacity 0.18s ease,
            visibility 0s;
    }

    .app-shell :global(.primary-nav-rail),
    .app-shell :global(.mobile-tab-strip) {
        transform: none;
        transition: transform 0.24s ease;
    }

    .app-shell.reader-focus :global(.primary-navigation) {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
        transition:
            opacity 0.18s ease,
            visibility 0s 0.24s;
    }

    .app-shell.reader-focus :global(.primary-nav-rail) {
        transform: translateX(-100%);
    }

    @media only screen and (min-width: 1154px) {
        .app-shell :global(#navigator) {
            --safe-area-inset-top: 0rem;
            --safe-area-inset-bottom: 0rem;
            position: absolute !important;
            left: calc(var(--app-navigation-width) + 0.75rem) !important;
            top: 0.75rem !important;
            right: auto !important;
            bottom: 0.75rem !important;
            width: min(22rem, calc(100% - var(--app-navigation-width) - 1.5rem)) !important;
            min-width: min(22rem, calc(100% - var(--app-navigation-width) - 1.5rem)) !important;
            max-width: min(22rem, calc(100% - var(--app-navigation-width) - 1.5rem)) !important;
            height: auto !important;
            min-height: 0;
            transform: translateX(calc(-100% - 1rem)) !important;
            visibility: hidden;
            border: 1px solid var(--color-grey-100);
            border-radius: 0.65rem;
            background: var(--navigator-surface, var(--color-grey-0));
            box-shadow: none;
            overflow: hidden;
            transition:
                transform 0.28s ease,
                visibility 0s 0.28s,
                left 0.24s ease;
            z-index: 12;
        }

        .app-shell :global(#navigator.open) {
            transform: translateX(0) !important;
            visibility: visible;
            transition:
                transform 0.28s ease,
                left 0.24s ease;
        }

        :global(.darkmode) .app-shell :global(#navigator),
        :global(html.darkmode) .app-shell :global(#navigator),
        :global(.darkmode #navigator),
        :global(html.darkmode #navigator) {
            border-color: var(--color-grey-800);
            background: var(--navigator-surface, var(--color-grey-900));
        }

        :global(#navigator:not(.open)) {
            pointer-events: none;
        }

        :global(#navigator::before) {
            display: none !important;
        }

        :global(#navigator .close) {
            top: var(--spacing-panelpad);
            right: var(--spacing-panelpad);
            display: grid;
            place-items: center;
            margin: 0;
            opacity: 0.56;
            cursor: pointer;
        }

        :global(#navigator .close svg) {
            width: 1.1rem;
            height: 1.1rem;
        }

        :global(#navigator #navigatorScroll) {
            height: 100%;
            padding: var(--spacing-panelpad);
            padding-top: var(--spacing-panelpad);
            padding-bottom: var(--spacing-panelpad);
            background: var(--navigator-surface, var(--color-grey-0));
        }

        :global(#navigator .search),
        :global(#navigator .search::after) {
            background-color: var(--navigator-surface, var(--color-grey-0));
        }

        :global(.darkmode #navigator .search),
        :global(.darkmode #navigator .search::after),
        :global(html.darkmode #navigator .search),
        :global(html.darkmode #navigator .search::after) {
            background-color: var(--navigator-surface, var(--color-grey-900));
        }

        :global(#navigator #navigatorScroll .search) {
            top: calc(-1 * var(--spacing-panelpad));
            padding-top: var(--spacing-panelpad);
            margin-top: calc(-1 * var(--spacing-panelpad));
        }

        :global(#navigator #navigatorScroll .navigator-search-shell) {
            width: calc(100% - var(--navigator-search-close-clearance));
        }

        :global(.navigator--stop-shift) {
            will-change: transform;
        }
    }

    @media only screen and (max-width: 1153px) {
        .app-shell {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: minmax(0, 1fr) auto;
            grid-template-areas: "main" "primary";
        }

        .app-shell.reader-focus {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: minmax(0, 1fr) 0;
        }

        .app-shell.reader-focus :global(.primary-nav-rail) {
            transform: translateY(100%);
        }

        .app-shell.reader-focus :global(.mobile-tab-strip) {
            transform: translateY(calc(100% + var(--app-primary-nav-height)));
        }

        .app-main {
            min-height: 0;
        }

        .app-shell :global(#navigator) {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: auto !important;
            bottom: calc(
                var(--app-primary-nav-height) + var(--app-reader-tabs-dock-height)
            ) !important;
            z-index: 12;
            width: min(calc(100vw - 1rem), 28.125rem) !important;
            min-width: 0 !important;
            max-width: min(calc(100vw - 1rem), 28.125rem) !important;
            height: auto !important;
            border: 0;
            border-right: 1px solid var(--color-grey-100);
            border-radius: 0;
            box-shadow: none;
            transform: translateX(calc(-100% - 1rem)) !important;
            visibility: hidden;
            overflow: hidden;
            transition:
                transform 0.24s ease,
                visibility 0s 0.24s;
        }

        .app-shell :global(#navigator.open) {
            transform: translateX(0) !important;
            visibility: visible;
            transition: transform 0.24s ease;
        }

        :global(.darkmode) .app-shell :global(#navigator),
        :global(html.darkmode) .app-shell :global(#navigator) {
            border-right-color: var(--color-grey-800);
            background: var(--navigator-surface, var(--color-grey-900));
        }

        .app-shell :global(.navigatorBack.navigator-backdrop--contents) {
            position: absolute;
            top: 0;
            right: 0;
            bottom: calc(var(--app-primary-nav-height) + var(--app-reader-tabs-dock-height));
            left: 0;
            z-index: 4;
            background-color: rgb(255 255 255 / 0.62);
        }

        :global(.darkmode) .app-shell :global(.navigatorBack.navigator-backdrop--contents) {
            background-color: rgb(0 0 0 / 0.62);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .app-shell,
        .app-shell :global(.primary-navigation),
        .app-shell :global(.primary-nav-rail),
        .app-shell :global(.mobile-tab-strip) {
            transition: none;
        }
    }
</style>
